import { t as uiText } from '../i18n';
import { uiLanguageFromI18n } from '../i18n/uiLanguage';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { PAID_TERMS_VERSION, paidLegalPageUrls } from '../features/plans/termsConsent';
import { isNcgSeries } from '../features/plans/series';
import { NcgLaunchOfferNotice, PaidPlanRenewalNotice } from './billing/NcgLaunchOfferNotice';

type PlanId = 'free' | 'starter' | 'standard' | 'team';

type WorkspaceBillingInfo = {
  name?: string;
  planId?: string;
  seriesId?: string;
  parentWorkspaceId?: string;
  stripeSubscriptionId?: string;
};

type CreateCheckoutOrUpdateSubscriptionResponse =
  | { kind: 'checkout'; url: string }
  | { kind: 'priceChanged'; campaign: boolean }
  | { kind: 'scheduled' | 'cancelled' | 'updated'; message?: string };

function normalizePlanId(input: unknown): PlanId {
  const raw = typeof input === 'string' ? input.trim().toLowerCase() : '';
  if (raw === 'starter' || raw === 'standard' || raw === 'team') return raw;
  return 'free';
}

function openInNewTab(path: string) {
  const url = `${window.location.origin}${path}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export default function PlanChangeFlowModal(props: {
  isOpen: boolean;
  workspaceId: string;
  targetPlanId: PlanId;
  /** 画面に表示していたキャンペーン価格の適用状態。未指定なら価格の再確認は行わない */
  expectedCampaign?: boolean;
  onClose: () => void;
}) {
  const { isOpen, workspaceId, targetPlanId, onClose } = props;
  const { i18n } = useTranslation();
  const legalPages = paidLegalPageUrls(uiLanguageFromI18n(i18n.resolvedLanguage || i18n.language));
  // 同意済みの価格状態。Checkout 作成時にサーバー判定と食い違えば更新し、再確認させる
  const [confirmedCampaign, setConfirmedCampaign] = React.useState<boolean | undefined>(props.expectedCampaign);
  const [priceChangedNotice, setPriceChangedNotice] = React.useState<boolean | null>(null);
  const { user } = useAuthContext();
  const { refresh } = useWorkspaceContext();

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  const [workspaceInfo, setWorkspaceInfo] = React.useState<WorkspaceBillingInfo | null>(null);
  const currentPlanId = normalizePlanId(workspaceInfo?.planId);

  const isChildWorkspace = !!workspaceInfo?.parentWorkspaceId;

  const mode = import.meta.env.MODE === 'production' ? 'live' : 'test';

  React.useEffect(() => {
    if (!isOpen) return;
    setTermsAccepted(false);
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, 'workspaces', workspaceId));
        if (!snap.exists()) {
          throw new Error(uiText("Workspace not found"));
        }
        const data = snap.data() as any;
        const info: WorkspaceBillingInfo = {
          name: typeof data?.name === 'string' ? data.name : undefined,
          planId: typeof data?.planId === 'string' ? data.planId : undefined,
          seriesId: typeof data?.seriesId === 'string' ? data.seriesId : undefined,
          parentWorkspaceId: typeof data?.parentWorkspaceId === 'string' ? data.parentWorkspaceId : undefined,
          stripeSubscriptionId:
            typeof data?.stripeSubscriptionId === 'string' ? data.stripeSubscriptionId.trim() : undefined,
        };
        if (!cancelled) setWorkspaceInfo(info);
      } catch (e: any) {
        console.error('[PlanChangeFlowModal] load workspace failed', e);
        if (!cancelled) setError(e?.message || uiText("Couldn't load"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, workspaceId]);

  if (!isOpen) return null;

  // 有料プランの新規決済開始では、利用規約への同意が必須
  const needsTermsConsent = targetPlanId !== 'free';
  const resubscribeAtRegularPrice =
    currentPlanId === 'free' && !!workspaceInfo?.stripeSubscriptionId;
  const showLaunchTerms =
    !resubscribeAtRegularPrice &&
    targetPlanId === 'starter' &&
    isNcgSeries(workspaceInfo?.seriesId) &&
    (priceChangedNotice === true || (priceChangedNotice === null && confirmedCampaign === true));
  const canProceed =
    !!user?.uid &&
    !submitting &&
    !loading &&
    currentPlanId !== targetPlanId &&
    (!needsTermsConsent || termsAccepted);

  const submit = async () => {
    setError(null);

    if (!user?.uid) {
      setError(uiText("Sign in to do this."));
      return;
    }

    if (isChildWorkspace) return;

    if (needsTermsConsent && !termsAccepted) return;

    try {
      setSubmitting(true);
      const fn = httpsCallable(functions, 'createCheckoutOrUpdateSubscriptionFn');

      const res = await fn({
        workspaceId,
        targetPlanId,
        mode,
        ...(needsTermsConsent ? { termsAccepted: true, termsVersion: PAID_TERMS_VERSION } : {}),
        returnUrl: `${window.location.origin}/${workspaceId}`,
        ...(resubscribeAtRegularPrice
          ? { expectedCampaign: false }
          : typeof confirmedCampaign === 'boolean'
            ? { expectedCampaign: confirmedCampaign }
            : {}),
      });

      const data = res.data as any as CreateCheckoutOrUpdateSubscriptionResponse;
      if (data?.kind === 'priceChanged') {
        // 料金が変わったので決済へ進まず、変更後の料金を見せて再確認する
        setConfirmedCampaign(data.campaign);
        setPriceChangedNotice(data.campaign);
        return;
      }
      if (data?.kind === 'checkout' && data?.url) {
        window.location.assign(data.url);
        return;
      }

      const message =
        (data && typeof (data as any).message === 'string' && (data as any).message.trim())
          ? (data as any).message.trim()
          : uiText("Processing complete.");
      alert(message);
      await refresh();
      onClose();
    } catch (e: any) {
      console.error('[PlanChangeFlowModal] submit failed', e);
      const msg = typeof e?.message === 'string' ? e.message : '';
      const code = typeof e?.code === 'string' ? e.code : '';
      if (code === 'failed-precondition') {
        setError(msg || uiText("Stripe setup is incomplete."));
        return;
      }
      if (code === 'functions/unavailable' || code === 'unavailable') {
        // 料金設定を確認できないときはサーバーが決済開始を止める（メッセージはサーバー側の文言）
        setError(msg || uiText("Failed to start the plan change. Please try again later."));
        return;
      }
      if (code === 'permission-denied') {
        setError(msg || uiText("This action requires workspace admin permission."));
        return;
      }
      setError(uiText("Failed to start the plan change. Please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="upgrade-modal-overlay"
      onClick={onClose}
      style={{ zIndex: 100000 }}
    >
      <div
        className="upgrade-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div className="upgrade-modal-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{uiText("Change plan")}</h2>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>
              {uiText("Target:")} <span style={{ color: '#333' }}>{workspaceInfo?.name || workspaceId}</span>
            </div>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>
              {uiText("Current:")} <span style={{ color: '#333' }}>{currentPlanId}</span> {uiText("→ Change to:")} <span style={{ color: '#333' }}>{targetPlanId}</span>
            </div>
          </div>
          <button className="upgrade-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="upgrade-modal-body" style={{ paddingTop: 12 }}>
          {loading ? (
            <div style={{ fontSize: 13, color: '#666' }}>{uiText("Loading now...")}</div>
          ) : error ? (
            <div style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(211,47,47,0.35)', background: 'rgba(211,47,47,0.06)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
              {error}
            </div>
          ) : null}

          {isChildWorkspace && !loading && (
            <div style={{ marginTop: 12 }}>
              <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: 'rgba(255,255,255,0.70)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                {uiText("This child workspace cannot change its plan on its own.")}
                <br />
                {uiText("Parent workspace (")}{workspaceInfo?.parentWorkspaceId}{uiText("). Please change its plan.")}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  type="button"
                  className="plan-button upgrade"
                  onClick={() => {
                    const parentId = workspaceInfo?.parentWorkspaceId;
                    if (!parentId) return;
                    openInNewTab(`/${parentId}`);
                  }}
                >
                  {uiText("Open parent workspace")}
                </button>
                <button
                  type="button"
                  className="plan-button upgrade"
                  onClick={() => {
                    const parentId = workspaceInfo?.parentWorkspaceId;
                    if (!parentId) return;
                    window.location.assign(`/${parentId}`);
                  }}
                >
                  {uiText("Switch to parent")}
                </button>
              </div>
            </div>
          )}

          {!isChildWorkspace && !loading && (
            <>
              {targetPlanId === 'free' ? (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: 'rgba(255,255,255,0.70)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                  {uiText("Changing to free cancels the paid plan subscription at the end of the period (applied at period end, no proration).")}
                </div>
              ) : (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: 'rgba(255,255,255,0.70)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                  {uiText("You will continue to Stripe Checkout to start this paid plan.")}
                </div>
              )}

              {needsTermsConsent && (
                <label
                  style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.6, color: '#2b2b2b', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={submitting}
                    style={{ marginTop: 4 }}
                  />
                  <span>
                    <a href={legalPages.terms} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      {uiText("Terms of Service")}
                    </a>
                    {uiText(", ")}
                    <a href={legalPages.privacy} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      {uiText("Privacy Policy")}
                    </a>
                    {uiText(", and ")}
                    <a href={legalPages.tokusho} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      {uiText("Specified Commercial Transactions Act")}
                    </a>
                    {uiText(" — I agree. Starting a paid plan moves you from the Trial Terms to the Terms of Service.")}
                  </span>
                </label>
              )}

              {showLaunchTerms && priceChangedNotice === true && <NcgLaunchOfferNotice />}

              {priceChangedNotice !== null && (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(245,124,0,0.5)', background: 'rgba(245,124,0,0.08)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                  {priceChangedNotice
                    ? uiText("The price has changed. The launch offer now applies: ¥9,800/month (excl. tax) for the first 12 months, then ¥14,800/month. Please review and press the button again to continue.")
                    : uiText("The price has changed. The launch offer is no longer available, so the regular price of ¥14,800/month applies. Please review and press the button again to continue.")}
                </div>
              )}

              {priceChangedNotice === null && confirmedCampaign === true && targetPlanId === 'starter' && (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: '#fffaf6', color: '#2b2b2b', lineHeight: 1.5 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#b5532f' }}>{uiText("Launch offer")}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#b5532f' }}>
                    ¥9,800{uiText("/month")}
                    <span style={{ fontSize: 12, fontWeight: 400, color: '#666', marginLeft: 4 }}>{uiText("(excl. tax)")}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, marginLeft: 8, color: '#5c4a3d' }}>{uiText("For the first 12 months")}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{uiText("From month 13: ¥14,800/month (excl. tax)")}</div>
                  <NcgLaunchOfferNotice />
                </div>
              )}

              {targetPlanId !== 'free' && !showLaunchTerms && (
                <PaidPlanRenewalNotice seriesId={workspaceInfo?.seriesId} planId={targetPlanId} />
              )}
              {resubscribeAtRegularPrice && isNcgSeries(workspaceInfo?.seriesId) && targetPlanId === 'starter' && (
                <NcgLaunchOfferNotice part="resubscribe" />
              )}

              {currentPlanId === targetPlanId && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                  {uiText("You are already on this plan.")}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="plan-button secondary" onClick={onClose} disabled={submitting}>
                  {uiText("Cancel")}
                </button>
                <button
                  type="button"
                  className="plan-button cta"
                  onClick={submit}
                  disabled={!canProceed}
                >
                  {submitting ? uiText("Processing...") : (targetPlanId === 'free' ? uiText("Schedule cancellation") : uiText("Start payment"))}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
